import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Stack,
  useColorModeValue,
} from '@chakra-ui/react';
import { getBackendURL, setAndClear, apiFetch } from '../../../utils/utils';
import { Link, useNavigate } from 'react-router-dom';

const AVAILABLE_ROUTES = [
  { key: 'home', label: 'Home / Dashboard' },
  { key: 'digi-card-sites', label: 'Digi Card — Sites' },
  { key: 'digi-card-templates', label: 'Digi Card — Templates' },
  { key: 'distributors', label: 'Distributors' },
  { key: 'distributors-add', label: 'Add Distributor' },
  { key: 'distributors-plans', label: 'Distributor Plans' },
  { key: 'distributors-payments', label: 'Distributor Payments' },
  { key: 'merchants', label: 'Merchants' },
  { key: 'merchants-add', label: 'Add Merchant' },
  { key: 'merchants-plans', label: 'Merchant Plans' },
  { key: 'merchants-payments', label: 'Merchant Payments' },
  { key: 'settings', label: 'Settings' },
];

function AddSupportUserPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [assignedRoutes, setAssignedRoutes] = useState([]);
  const [allowedSitesCount, setAllowedSitesCount] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inProgress, setInProgress] = useState(false);

  const handleRouteCheckChange = (routeKey) => {
    setAssignedRoutes(prev =>
      prev.includes(routeKey) ? prev.filter(r => r !== routeKey) : [...prev, routeKey]
    );
  };

  const onSubmit = () => {
    setError('');
    if (!email) {
      setError('Please enter the email');
      return;
    }
    if (email.indexOf('@gmail.com') === -1) {
      setError('Please use a Gmail account');
      return;
    }
    if (assignedRoutes.length === 0) {
      setError('Please assign at least one route');
      return;
    }

    setInProgress(true);
    apiFetch(`${getBackendURL()}/admin/support/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        assignedRoutes,
        allowedSitesCount,
      }),
    })
      .then(res => res.json())
      .then(res => {
        setInProgress(false);
        if (res.supportUser) {
          setAndClear(res.message, setSuccess);
          setTimeout(() => {
            navigate('/support-users');
          }, 1000);
        } else {
          setAndClear(res.message, setError);
        }
      })
      .catch(err => {
        setInProgress(false);
        setAndClear(err.toString(), setError);
      });
  };

  return (
    <>
      <Breadcrumb marginTop={2} marginLeft={2}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/">Home</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/support-users">Support Users</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/support-users/add">Add</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Stack mt={4} direction={{ base: 'column', lg: 'row' }}>
        <Box w={'100%'}>
          <Card bg={useColorModeValue('white', 'gray.700')} data-testid="add-support-user-form-card">
            <CardHeader>
              <Heading size="md">Add Support User</Heading>
            </CardHeader>
            <Divider />
            <CardBody>
              <Alert status="success" display={success ? 'flex' : 'none'}>
                <AlertIcon />
                {success}
              </Alert>
              <Alert status="error" display={error ? 'flex' : 'none'}>
                <AlertIcon />
                {error}
              </Alert>
              <Stack spacing="4" mt="2">
                <FormControl isRequired>
                  <FormLabel>Google Email</FormLabel>
                  <Input
                    type="email"
                    id="support-email"
                    name="email"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                  />
                  <FormHelperText>Google Email for support user login.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Assigned features</FormLabel>
                  <FormHelperText mb={2}>
                    Select the features this support user should be able to access.
                  </FormHelperText>
                  <CheckboxGroup>
                    <Stack spacing={2}>
                      {AVAILABLE_ROUTES.map(route => (
                        <Checkbox
                          key={route.key}
                          isChecked={assignedRoutes.includes(route.key)}
                          onChange={() => handleRouteCheckChange(route.key)}
                        >
                          {route.label}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </FormControl>

                <FormControl>
                  <FormLabel>Allowed Sites Count</FormLabel>
                  <NumberInput
                    min={0}
                    value={allowedSitesCount}
                    onChange={(_, val) => setAllowedSitesCount(val)}
                    maxW="200px"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText>Maximum number of sites this support user can create on behalf of merchants.</FormHelperText>
                </FormControl>
              </Stack>
            </CardBody>
            <Divider />
            <CardFooter>
              <ButtonGroup spacing="2">
                <Button
                  variant="solid"
                  colorScheme="teal"
                  onClick={onSubmit}
                  isLoading={inProgress}
                  loadingText="Submitting..."
                >
                  Add Support User
                </Button>
              </ButtonGroup>
            </CardFooter>
          </Card>
        </Box>
      </Stack>
    </>
  );
}

export default AddSupportUserPage;
